import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utility/axiosInstance';

export interface Cluster {
    id: string;
    stack_name: string;
    stack_status: string;
    creation_time: string;
    updated_time: string;
    description: string;
    links: { rel: string; href: string }[];
    // Build progress. An HA cluster is not created at size 3: one member comes up and the rest are
    // ramped on one at a time (concurrent creation defeats the anti-affinity spread), so without
    // these the page can only say "CREATING" for several minutes. target_members is the size the
    // user asked for, members_ready how many are serving right now. Both absent on single-member
    // clusters and on clusters created before this shipped - the UI falls back to the plain status.
    target_members?: number;
    members_ready?: number;
}

interface ClustersState {
    data: Cluster[];
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
}

const initialState: ClustersState = {
    data: [],
    status: 'idle',
    error: null,
};

export const fetchClusters = createAsyncThunk('clusters/fetchClusters', async () => {
    const response = await axiosInstance.get('/v1/clusters');
    return response.data;
});

const clustersSlice = createSlice({
    name: 'clusters',
    initialState,
    reducers: {
        updateCluster: (state, action) => {
            // A deleted cluster must never enter or survive in the store, whatever arrives on the
            // socket. The backend used to push `cluster:update` carrying a DELETE_COMPLETE stack
            // (Heat serves that status with HTTP 200 before the stack 404s), and the branch below
            // happily unshifted it back in - so a row the user had just watched disappear returned.
            // Guarding here as well as at the source, because this reducer resurrects ANY id it does
            // not recognise, which makes it the place a stale push does damage.
            if (String(action.payload?.stack_status ?? "").startsWith("DELETE_COMPLETE")) {
                state.data = state.data.filter((c) => c.id !== action.payload.id);
                return;
            }
            const index = state.data.findIndex((c) => c.id === action.payload.id);
            if (index !== -1) {
                const prev = state.data[index];
                // Carry build progress across pushes that don't carry it. Several points in the
                // create/ramp workflow push a plain Heat stack, and a blind replace would blank
                // "building 2 of 3" back to "UPDATING" between ramp steps.
                state.data[index] = {
                    ...action.payload,
                    target_members: action.payload.target_members ?? prev.target_members,
                    members_ready: action.payload.members_ready ?? prev.members_ready,
                };
            } else {
                state.data.unshift(action.payload);
            }
        },
        removeCluster: (state, action) => {
            state.data = state.data.filter((c) => c.id !== action.payload.id);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchClusters.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchClusters.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = action.payload;
            })
            .addCase(fetchClusters.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'Failed to fetch clusters';
            });
    },
});

export const { updateCluster, removeCluster } = clustersSlice.actions;
export default clustersSlice.reducer;
